import React from "react";
import { useT } from "@/context/I18nContext";
import LanguageToggle from "@/components/layout/LanguageToggle";
import BrandLogo from "@/components/common/BrandLogo";

/** Split-screen auth shell — imagery on the right, form on the left. */
export default function AuthLayout({ children, eyebrow, title, subtitle, footer }) {
  const t = useT();
  return (
    <div className="min-h-screen flex bg-cream-50">
      <div className="flex-1 flex flex-col p-8 md:p-14 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <BrandLogo size="xl" />
            <div className="leading-tight">
              <div className="font-heading font-semibold text-ink-900 tracking-tight text-lg">
                {t("brand.name")}
              </div>
              <div className="text-[11px] text-ink-500">{t("brand.subtitle")}</div>
            </div>
          </div>
          <LanguageToggle />
        </div>

        <div className="flex-1 flex items-center max-w-md mx-auto w-full py-10">
          <div className="w-full animate-fade-up">
            {eyebrow && (
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500 mb-3">
                {eyebrow}
              </div>
            )}
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink-900 leading-none">
              {title}
            </h1>
            {subtitle && <p className="mt-3 text-ink-500">{subtitle}</p>}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-6 text-sm text-ink-500">{footer}</div>}
          </div>
        </div>

        <div className="text-xs text-ink-300">
          <a
            href={process.env.REACT_APP_MARKETING_URL || "https://innercompass.tech"}
            target="_blank"
            rel="noreferrer"
            data-testid="auth-marketing-link"
            className="hover:text-ink-500 transition"
          >
            © Inner Compass · innercompass.tech
          </a>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-ink-400" data-testid="auth-legal-links">
            <a href="/privacy" className="hover:text-ink-700" data-testid="auth-link-privacy">{t("public.nav.privacy")}</a>
            <a href="/terms" className="hover:text-ink-700" data-testid="auth-link-terms">{t("public.nav.terms")}</a>
            <a href="/cookies" className="hover:text-ink-700" data-testid="auth-link-cookies">{t("public.nav.cookies")}</a>
            <a href="/delete-account" className="hover:text-ink-700" data-testid="auth-link-delete">{t("public.nav.deleteAccount")}</a>
            <a href="/ai-safety" className="hover:text-ink-700" data-testid="auth-link-ai-safety">{t("public.nav.aiSafety")}</a>
            <a href="/privacy-choices" className="hover:text-ink-700" data-testid="auth-link-privacy-choices">{t("public.nav.privacyChoices")}</a>
          </div>
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1719578833611-1e8170003a70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwd2FybSUyMHBsYW50JTIwc2hhZG93fGVufDB8fHx8MTc4MzQ4NzkzMHww&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-cream-50/40 via-transparent to-sage-500/10" />
        <div className="absolute bottom-14 left-14 right-14">
          <div className="bg-white/80 backdrop-blur rounded-2xl p-6 max-w-md shadow-card">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-sage-600 mb-2">
              {t("brand.subtitle")}
            </div>
            <p className="font-heading text-xl text-ink-900 tracking-tight leading-snug">
              {t("brand.tagline")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
